import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { sellerOrderApi } from '../../services/seller/orderApi';
import toast from 'react-hot-toast';
import { 
  Users, 
  TrendingUp, 
  DollarSign, 
  ShoppingCart,
  BarChart3,
  Calendar,
  Filter,
  Eye,
  Download,
  RefreshCw,
  TrendingDown,
  FileText,
  Printer,
  X,
  Package,
  User,
  Star,
  Clock,
  Tag,
  MapPin,
  Phone,
  Mail,
  Plus,
  Store,
  Palette,
  Shield
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement
);

const SellerDashboard = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [timeRange, setTimeRange] = useState('today');
  const [isChartFullscreen, setIsChartFullscreen] = useState(false);
  const [chartKey, setChartKey] = useState(0);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [isUsingCustomRange, setIsUsingCustomRange] = useState(false);
  
  // Stats state
  const [currentStats, setCurrentStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    totalProfit: 0,
    totalCost: 0,
    totalProductsSold: 0,
    totalCustomers: 0,
    pendingOrders: 0,
    completedOrders: 0,
    cancelledOrders: 0,
    revenueGrowth: 0,
    ordersGrowth: 0,
    customersGrowth: 0,
    profitGrowth: 0,
    costGrowth: 0
  });



  // Load data khi component mount
  useEffect(() => {
    loadDashboardData();
  }, [token]);

  // Cập nhật stats và chart khi timeRange thay đổi
  useEffect(() => {
    if (allOrders.length > 0) {
      updateStats();
      setChartKey(prev => prev + 1);
    }
  }, [timeRange, allOrders, customStartDate, customEndDate]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Đang gọi API seller orders...');
      const response = await sellerOrderApi.getSellerOrders(token);
      
      console.log('API Response:', response);
      
      if (response.data && response.data.success) {
        setAllOrders(response.data.orders || []);
      } else if (response.data && Array.isArray(response.data)) {
        setAllOrders(response.data);
      } else {
        console.error('Invalid API response format:', response);
        setError('Dữ liệu API không đúng định dạng');
        setAllOrders([]);
      }
      
      // toast.success('Đã tải dữ liệu dashboard!');
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError(`Lỗi khi tải dữ liệu: ${error.message}`);
      setAllOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Hàm lọc đơn hàng theo thời gian
  const getFilteredOrders = (timeRange = 'today') => {
    if (!allOrders || allOrders.length === 0) return [];
    
    const now = new Date();
    let startOfPeriod, endOfPeriod;
    
    switch (timeRange) {
      case 'today':
        startOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        endOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        break;
      case 'week':
        const dayOfWeek = now.getDay();
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        startOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysFromMonday);
        endOfPeriod = new Date(startOfPeriod.getFullYear(), startOfPeriod.getMonth(), startOfPeriod.getDate() + 6, 23, 59, 59, 999);
        break;
      case 'month':
        startOfPeriod = new Date(now.getFullYear(), now.getMonth(), 1);
        endOfPeriod = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        break;
      case 'year':
        startOfPeriod = new Date(now.getFullYear(), 0, 1);
        endOfPeriod = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          startOfPeriod = new Date(customStartDate);
          endOfPeriod = new Date(customEndDate);
          endOfPeriod.setHours(23, 59, 59, 999);
        } else {
          startOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          endOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        }
        break;
      default:
        startOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        endOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    }
    
    return allOrders.filter(order => {
      const orderDate = new Date(order.created_at || order.date_created);
      return orderDate >= startOfPeriod && orderDate <= endOfPeriod;
    });
  };

  const updateStats = () => {
    const filteredOrders = getFilteredOrders(timeRange);
    
    const totalOrders = filteredOrders.length;
    const totalRevenue = filteredOrders.reduce((sum, order) => sum + parseFloat(order.total || 0), 0);
    const totalCost = filteredOrders.reduce((sum, order) => sum + parseFloat(order.cost || 0), 0);
    const totalShipping = filteredOrders.reduce((sum, order) => sum + parseFloat(order.shipping_fee || order.shipping || 0), 0);
    const totalExpenses = totalCost + totalShipping;
    const totalProfit = totalRevenue - totalExpenses;
    const totalCostValue = totalCost + totalShipping; // Chi phí = shipping_fee + cost
    
    // Tính tổng số sản phẩm đã bán
    let totalProductsSold = 0;
    filteredOrders.forEach(order => {
      try {
        const products = JSON.parse(order.products || '[]');
        totalProductsSold += products.reduce((sum, product) => sum + parseInt(product.quantity || 1), 0);
      } catch (error) {
        console.error('Error parsing products:', error);
        totalProductsSold += 1; // Default to 1 if parsing fails
      }
    });
    
    const uniqueCustomers = new Set(
      filteredOrders
        .map(order => order.customer_email || order.customer_mail || order.email)
        .filter(email => email && email.trim() !== '')
    ).size;
    
    const pendingOrders = filteredOrders.filter(order => order.order_status === 'pending').length;
    const completedOrders = filteredOrders.filter(order => order.order_status === 'completed').length;
    const cancelledOrders = filteredOrders.filter(order => order.order_status === 'cancelled').length;
    
    // Tính growth (mock data for now)
    const revenueGrowth = 12.5;
    const ordersGrowth = 8.3;
    const customersGrowth = 15.2;
    const profitGrowth = 8.7;
    const costGrowth = 5.2;
    
    setCurrentStats({
      totalOrders,
      totalRevenue,
      totalProfit,
      totalCost: totalCostValue,
      totalProductsSold,
      totalCustomers: uniqueCustomers,
      pendingOrders,
      completedOrders,
      cancelledOrders,
      revenueGrowth,
      ordersGrowth,
      customersGrowth,
      profitGrowth,
      costGrowth
    });
  };



  const getTopStoresBySales = () => {
    const filteredOrders = getFilteredOrders(timeRange);
    const storeMap = new Map();
    
    filteredOrders.forEach(order => {
      // Extract store name from referrer_url
      let storeName = 'Unknown Store';
      if (order.referrer_url) {
        try {
          const url = new URL(order.referrer_url);
          // Extract domain name (e.g., "bluebatcafellc" from "https://bluebatcafellc.com/")
          const domain = url.hostname.split('.')[0];
          storeName = domain.charAt(0).toUpperCase() + domain.slice(1);
        } catch (error) {
          console.error('Error parsing referrer_url:', error);
          storeName = 'Unknown Store';
        }
      }
      
      // Count items in the order
      let itemCount = 1; // Default to 1 if no products data
      try {
        const products = JSON.parse(order.products || '[]');
        itemCount = products.reduce((sum, product) => sum + parseInt(product.quantity || 1), 0);
      } catch (error) {
        console.error('Error parsing products:', error);
      }
      
      if (storeMap.has(storeName)) {
        const existing = storeMap.get(storeName);
        existing.orders += 1;
        existing.revenue += parseFloat(order.total || 0);
        existing.items += itemCount;
      } else {
        storeMap.set(storeName, {
          name: storeName,
          orders: 1,
          revenue: parseFloat(order.total || 0),
          items: itemCount
        });
      }
    });
    
    // Convert to array and sort by number of orders
    const topStores = Array.from(storeMap.values())
      .sort((a, b) => b.orders - a.orders)
      .slice(0, 5);
    
    return topStores;
  };

  const getTopProducts = () => {
    const filteredOrders = getFilteredOrders(timeRange);
    const productMap = new Map();
    
    filteredOrders.forEach(order => {
      try {
        const products = JSON.parse(order.products || '[]');
        products.forEach(product => {
          let productName = product.name || product.product_name || 'Unknown Product';
          
          if (productName.includes('SKU:')) {
            productName = productName.split('SKU:')[0].trim();
          }
          
          if (!productName || productName === 'Unknown Product') {
            return;
          }
          
          if (productMap.has(productName)) {
            const existing = productMap.get(productName);
            existing.sales += parseInt(product.quantity || 1);
            existing.revenue += parseFloat(product.total || product.price || 0);
            existing.count += 1;
          } else {
            productMap.set(productName, {
              name: productName,
              sales: parseInt(product.quantity || 1),
              revenue: parseFloat(product.total || product.price || 0),
              count: 1,
              growth: 0
            });
          }
        });
      } catch (error) {
        console.error('Error parsing products:', error);
      }
    });
    
    const topProducts = Array.from(productMap.values())
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 4)
      .map(product => ({
        id: product.name,
        name: product.name,
        sales: product.sales,
        revenue: product.revenue,
        growth: product.growth
      }));
    
    return topProducts;
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(parseFloat(amount));
  };

  const getGrowthIcon = (growth) => {
    if (growth > 0) {
      return <TrendingUp className="w-4 h-4 text-green-600" />;
    } else if (growth < 0) {
      return <TrendingDown className="w-4 h-4 text-red-600" />;
    }
    return <BarChart3 className="w-4 h-4 text-gray-600" />;
  };

  const getGrowthColor = (growth) => {
    if (growth > 0) return 'text-green-600';
    if (growth < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Chờ xử lý';
      case 'completed':
        return 'Hoàn thành';
      case 'processing':
        return 'Đang xử lý';
      case 'cancelled':
        return 'Đã hủy';
      default:
        return status;
    }
  };

  const handleTimeRangeChange = (newTimeRange) => {
    setTimeRange(newTimeRange);
    if (newTimeRange === 'custom') {
      setShowCustomDatePicker(true);
    } else {
      setShowCustomDatePicker(false);
      setCustomStartDate('');
      setCustomEndDate('');
      setIsUsingCustomRange(false);
      // Reset chart khi chuyển về các option khác
      setChartKey(prev => prev + 1);
    }
  };

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      const startDate = new Date(customStartDate);
      const endDate = new Date(customEndDate);
      const today = new Date();
      
      // Check if end date is not after today
      if (endDate > today) {
        toast.error('Ngày kết thúc không được sau hôm nay');
        return;
      }
      
      // Check if start date is not after end date
      if (startDate > endDate) {
        toast.error('Ngày bắt đầu không được sau ngày kết thúc');
        return;
      }
      
      setShowCustomDatePicker(false);
      setTimeRange('custom'); // Đảm bảo timeRange được set thành 'custom'
      setIsUsingCustomRange(true);
      
      // Force update chart và stats
      setTimeout(() => {
        setChartKey(prev => prev + 1);
        updateStats();
      }, 100);
      
      toast.success('Đã áp dụng khoảng thời gian tùy chỉnh');
    } else {
      toast.error('Vui lòng chọn cả ngày bắt đầu và ngày kết thúc');
    }
  };

  const getTodayString = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Chart data functions
  const getRevenueChartData = () => {
    const filteredOrders = getFilteredOrders(timeRange);
    
    if (!filteredOrders || filteredOrders.length === 0) {
      return {
        labels: ['Không có dữ liệu'],
        datasets: [
          {
                         label: 'Doanh thu (USD)',
            data: [0],
            borderColor: '#10B981',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            fill: true,
            tension: 0.4,
            pointBackgroundColor: '#10B981',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 6,
            pointHoverRadius: 8,
            yAxisID: 'y'
          },
          {
                         label: 'Đơn hàng',
            data: [0],
            borderColor: '#3B82F6',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: false,
            tension: 0.4,
            pointBackgroundColor: '#3B82F6',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            yAxisID: 'y1'
          }
        ]
      };
    }

    // Determine chart type based on time range
    let labels = [];
    let revenueData = [];
    let ordersData = [];

    if (timeRange === 'today') {
      // 24 hours for today
      for (let i = 0; i < 24; i++) {
        labels.push(`${i}:00`);
      }
      
      const hours = {};
      for (let i = 0; i < 24; i++) {
        hours[i] = { revenue: 0, orders: 0 };
      }
      
      filteredOrders.forEach(order => {
        const date = new Date(order.created_at || order.date_created);
        const hour = date.getHours();
        hours[hour].revenue += parseFloat(order.total || 0);
        hours[hour].orders += 1;
      });
      
             revenueData = Object.values(hours).map(d => d.revenue);
      ordersData = Object.values(hours).map(d => d.orders);
    } else if (timeRange === 'week') {
      // 7 days for week
      const dayNames = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];
      labels = dayNames;
      
      const days = {};
      for (let i = 0; i < 7; i++) {
        days[i] = { revenue: 0, orders: 0 };
      }
      
      filteredOrders.forEach(order => {
        const date = new Date(order.created_at || order.date_created);
        const dayOfWeek = date.getDay();
        days[dayOfWeek].revenue += parseFloat(order.total || 0);
        days[dayOfWeek].orders += 1;
      });
      
      revenueData = Object.values(days).map(d => d.revenue);
      ordersData = Object.values(days).map(d => d.orders);
    } else if (timeRange === 'month') {
      // 30 days for month
      for (let i = 1; i <= 30; i++) {
        labels.push(`Ngày ${i}`);
      }
      
      const days = {};
      for (let i = 1; i <= 30; i++) {
        days[i] = { revenue: 0, orders: 0 };
      }
      
      filteredOrders.forEach(order => {
        const date = new Date(order.created_at || order.date_created);
        const day = date.getDate();
        if (days[day]) {
          days[day].revenue += parseFloat(order.total || 0);
          days[day].orders += 1;
        }
      });
      
      revenueData = Object.values(days).map(d => d.revenue);
      ordersData = Object.values(days).map(d => d.orders);
    } else if (timeRange === 'year') {
      // 12 months for year
      const monthNames = ['T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12'];
      labels = monthNames;
      
      const months = {};
      for (let i = 0; i < 12; i++) {
        months[i] = { revenue: 0, orders: 0 };
      }
      
      filteredOrders.forEach(order => {
        const date = new Date(order.created_at || order.date_created);
        const month = date.getMonth();
        months[month].revenue += parseFloat(order.total || 0);
        months[month].orders += 1;
      });
      
      revenueData = Object.values(months).map(d => d.revenue);
      ordersData = Object.values(months).map(d => d.orders);
    } else if (timeRange === 'custom') {
      // Custom date range - determine based on number of days
      const startDate = new Date(customStartDate);
      const endDate = new Date(customEndDate);
      const diffTime = Math.abs(endDate - startDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays <= 1) {
        // 24 hours for 1 day or less
        for (let i = 0; i < 24; i++) {
          labels.push(`${i}:00`);
        }
        
        const hours = {};
        for (let i = 0; i < 24; i++) {
          hours[i] = { revenue: 0, orders: 0 };
        }
        
        filteredOrders.forEach(order => {
          const date = new Date(order.created_at || order.date_created);
          const hour = date.getHours();
          hours[hour].revenue += parseFloat(order.total || 0);
          hours[hour].orders += 1;
        });
        
        revenueData = Object.values(hours).map(d => d.revenue);
        ordersData = Object.values(hours).map(d => d.orders);
      } else if (diffDays <= 7) {
        // 7 days for 1 week or less
        const dayNames = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];
        labels = dayNames;
        
        const days = {};
        for (let i = 0; i < 7; i++) {
          days[i] = { revenue: 0, orders: 0 };
        }
        
        filteredOrders.forEach(order => {
          const date = new Date(order.created_at || order.date_created);
          const dayOfWeek = date.getDay();
          days[dayOfWeek].revenue += parseFloat(order.total || 0);
          days[dayOfWeek].orders += 1;
        });
        
        revenueData = Object.values(days).map(d => d.revenue);
        ordersData = Object.values(days).map(d => d.orders);
      } else if (diffDays <= 30) {
        // 30 days for 1 month or less
        for (let i = 1; i <= 30; i++) {
          labels.push(`Ngày ${i}`);
        }
        
        const days = {};
        for (let i = 1; i <= 30; i++) {
          days[i] = { revenue: 0, orders: 0 };
        }
        
        filteredOrders.forEach(order => {
          const date = new Date(order.created_at || order.date_created);
          const day = date.getDate();
          if (days[day]) {
            days[day].revenue += parseFloat(order.total || 0);
            days[day].orders += 1;
          }
        });
        
        revenueData = Object.values(days).map(d => d.revenue);
        ordersData = Object.values(days).map(d => d.orders);
      } else if (diffDays <= 365) {
        // 12 months for 1 year or less
        const monthNames = ['T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12'];
        labels = monthNames;
        
        const months = {};
        for (let i = 0; i < 12; i++) {
          months[i] = { revenue: 0, orders: 0 };
        }
        
        filteredOrders.forEach(order => {
          const date = new Date(order.created_at || order.date_created);
          const month = date.getMonth();
          months[month].revenue += parseFloat(order.total || 0);
          months[month].orders += 1;
        });
        
        revenueData = Object.values(months).map(d => d.revenue);
        ordersData = Object.values(months).map(d => d.orders);
      } else {
        // Years for more than 1 year
        const startYear = startDate.getFullYear();
        const endYear = endDate.getFullYear();
        const yearCount = endYear - startYear + 1;
        
        for (let i = 0; i < yearCount; i++) {
          labels.push(`${startYear + i}`);
        }
        
        const years = {};
        for (let i = 0; i < yearCount; i++) {
          years[startYear + i] = { revenue: 0, orders: 0 };
        }
        
        filteredOrders.forEach(order => {
          const date = new Date(order.created_at || order.date_created);
          const year = date.getFullYear();
          if (years[year]) {
            years[year].revenue += parseFloat(order.total || 0);
            years[year].orders += 1;
          }
        });
        
        revenueData = Object.values(years).map(d => d.revenue);
        ordersData = Object.values(years).map(d => d.orders);
      }
    }

    return {
      labels: labels,
      datasets: [
        {
                     label: 'Doanh thu (USD)',
           data: revenueData,
          borderColor: '#10B981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#10B981',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 6,
          pointHoverRadius: 8,
          yAxisID: 'y'
        },
        {
                     label: 'Đơn hàng',
           data: ordersData,
          borderColor: '#3B82F6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: false,
          tension: 0.4,
          pointBackgroundColor: '#3B82F6',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          yAxisID: 'y1'
        }
      ]
    };
  };



  // Chart options
  const combinedChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 11,
          },
          boxWidth: 8,
          boxHeight: 8,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#fff',
        borderWidth: 1,
        titleFont: {
          size: 12,
        },
        bodyFont: {
          size: 11,
        },
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
                         if (context.parsed.y !== null) {
                               if (context.dataset.label === 'Doanh thu (USD)') {
                  label += formatCurrency(context.parsed.y);
                } else if (context.dataset.label === 'Đơn hàng') {
                  label += `${context.parsed.y} đơn hàng`;
                } else {
                 label += formatCurrency(context.parsed.y);
               }
             }
            return label;
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#6B7280',
          font: {
            size: 10,
          },
          maxRotation: 45,
          minRotation: 0,
        },
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        grid: {
          color: '#E5E7EB',
          borderDash: [5, 5],
        },
        ticks: {
          color: '#6B7280',
          font: {
            size: 10,
          },
                     callback: function(value) {
             return formatCurrency(value);
           }
        },
        title: {
          display: true,
                     text: 'Doanh thu (USD)',
          color: '#6B7280',
          font: {
            size: 10,
          },
        },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          color: '#3B82F6',
          font: {
            size: 10,
          },
        },
        title: {
          display: true,
                     text: 'Đơn hàng',
          color: '#3B82F6',
          font: {
            size: 10,
          },
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
    elements: {
      point: {
        radius: 3,
        hoverRadius: 5,
      },
      line: {
        borderWidth: 2,
      },
    },
  };



     return (
     <div className="space-y-4 lg:space-y-6 dashboard-container px-4 sm:px-6 lg:px-0 pt-4 lg:pt-6">
             {/* Header */}
       <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
         <div className="flex items-center justify-between lg:justify-start w-full lg:w-auto">
           <div>
             <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Thống kê</h1>
            
           </div>
           <button 
             onClick={loadDashboardData}
             className="lg:hidden p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
             title="Làm mới dữ liệu"
           >
             <RefreshCw className="w-5 h-5" />
           </button>
         </div>
         <div className="flex flex-col lg:flex-row items-center space-y-2 lg:space-y-0 lg:space-x-3 w-full lg:w-auto">
                      {/* Time Range Selector */}
           <div className="flex flex-col lg:flex-row items-center space-y-2 lg:space-y-0 lg:space-x-2 w-full lg:w-auto">
                           <select
                value={timeRange}
                onChange={(e) => handleTimeRangeChange(e.target.value)}
                className="w-full lg:w-auto px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="today">Hôm nay</option>
                <option value="week">Tuần này</option>
                <option value="month">Tháng này</option>
                <option value="year">Năm này</option>
                <option value="custom">
                  {isUsingCustomRange && customStartDate && customEndDate 
                    ? `Tùy chỉnh (${customStartDate} - ${customEndDate})`
                    : 'Tùy chỉnh'
                  }
                </option>
              </select>
           </div>

           {/* Custom Date Picker Modal */}
           {showCustomDatePicker && (
             <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
               <div className="bg-white p-6 rounded-xl shadow-lg max-w-md w-full mx-4">
                 <div className="flex items-center justify-between mb-4">
                   <h3 className="text-lg font-semibold text-gray-900">Chọn khoảng thời gian</h3>
                   <button
                     onClick={() => setShowCustomDatePicker(false)}
                     className="text-gray-400 hover:text-gray-600"
                   >
                     <X className="w-5 h-5" />
                   </button>
                 </div>
                 
                 <div className="space-y-4">
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">
                       Từ ngày
                     </label>
                     <input
                       type="date"
                       value={customStartDate}
                       onChange={(e) => setCustomStartDate(e.target.value)}
                       max={getTodayString()}
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                     />
                   </div>
                   
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">
                       Đến ngày
                     </label>
                     <input
                       type="date"
                       value={customEndDate}
                       onChange={(e) => setCustomEndDate(e.target.value)}
                       max={getTodayString()}
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                     />
                   </div>
                 </div>
                 
                 <div className="flex space-x-3 mt-6">
                   <button
                     onClick={() => setShowCustomDatePicker(false)}
                     className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                   >
                     Hủy
                   </button>
                   <button
                     onClick={handleCustomDateApply}
                     className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                   >
                     Áp dụng
                   </button>
                 </div>
               </div>
             </div>
           )}

                                           {/* Action Buttons */}
           <div className="flex flex-col lg:flex-row items-center space-y-2 lg:space-y-0 lg:space-x-2 w-full lg:w-auto">
             <button 
               onClick={loadDashboardData}
               className="hidden lg:block p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
               title="Làm mới dữ liệu"
             >
               <RefreshCw className="w-5 h-5" />
             </button>
           
           </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-white p-8 rounded-xl shadow-lg text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Đang tải dữ liệu...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 p-4 rounded-xl">
          <p className="text-red-600">{error}</p>
          <button 
            onClick={loadDashboardData}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Thử lại
          </button>
        </div>
      )}

                           {/* User Profile Section */}
             

             {/* Stats Cards - Responsive Grid */}
       {!loading && !error && (
         <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stats-cards">
                       <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 lg:p-6 rounded-xl shadow-lg text-white">
              <div className="flex items-center justify-between">
                <div>
                                     <p className="text-blue-100 text-xs font-medium">Đơn hàng & Sản phẩm</p>
                  <p className="text-lg lg:text-3xl font-bold">{currentStats.totalOrders.toLocaleString()}</p>
                                     <p className="text-blue-200 text-xs">{currentStats.totalProductsSold} sản phẩm đã bán</p>
                </div>
                <div className="p-1.5 lg:p-3 bg-blue-400 rounded-lg">
                  <ShoppingCart className="w-5 h-5 lg:w-8 lg:h-8" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-green-600 p-3 lg:p-6 rounded-xl shadow-lg text-white">
              <div className="flex items-center justify-between">
                <div>
                                     <p className="text-green-100 text-xs font-medium">Doanh thu</p>
                  <p className="text-lg lg:text-3xl font-bold">{formatCurrency(currentStats.totalRevenue)}</p>
                                     <p className="text-green-200 text-xs">{currentStats.revenueGrowth}% so với tháng trước</p>
                </div>
                <div className="p-1.5 lg:p-3 bg-green-400 rounded-lg">
                  <DollarSign className="w-5 h-5 lg:w-8 lg:h-8" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-3 lg:p-6 rounded-xl shadow-lg text-white">
              <div className="flex items-center justify-between">
                <div>
                                     <p className="text-purple-100 text-xs font-medium">Lợi nhuận</p>
                  <p className="text-lg lg:text-3xl font-bold">{formatCurrency(currentStats.totalProfit)}</p>
                                     <p className="text-purple-200 text-xs">{currentStats.profitGrowth}% so với tháng trước</p>
                </div>
                <div className="p-1.5 lg:p-3 bg-purple-400 rounded-lg">
                  <TrendingUp className="w-5 h-5 lg:w-8 lg:h-8" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-3 lg:p-6 rounded-xl shadow-lg text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-xs font-medium">Chi phí</p>
                  <p className="text-lg lg:text-3xl font-bold">{formatCurrency(currentStats.totalCost)}</p>
                                     <p className="text-orange-200 text-xs">{currentStats.costGrowth}% so với tháng trước</p>
                </div>
                <div className="p-1.5 lg:p-3 bg-orange-400 rounded-lg">
                  <TrendingDown className="w-5 h-5 lg:w-8 lg:h-8" />
                </div>
              </div>
            </div>
         </div>
       )}

      {/* Main Revenue Chart - Full Width */}
      {!loading && !error && (
        <div className={`bg-white p-4 lg:p-6 rounded-xl shadow-lg border chart-section ${isChartFullscreen ? 'chart-fullscreen' : ''}`}>
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-4 lg:mb-6">
                         <div className="mb-4 lg:mb-0">
                               <h3 className="text-lg lg:text-xl font-semibold text-gray-900">Biểu đồ Doanh thu & Đơn hàng</h3>
                               <p className="text-sm text-gray-600 mt-1">
                  Dữ liệu dựa trên khoảng thời gian đã chọn
                </p>
             </div>
                         <div className="hidden lg:flex items-center space-x-2">
                                               <span className="text-xs lg:text-sm text-gray-600">
                   <span className="inline-block w-3 h-3 bg-green-500 rounded-full mr-1"></span>
                   Doanh thu
                 </span>
                 <span className="text-xs lg:text-sm text-gray-600">
                   <span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-1"></span>
                   Đơn hàng
                 </span>
              <button
                onClick={() => setIsChartFullscreen(!isChartFullscreen)}
                className="p-1.5 lg:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title={isChartFullscreen ? "Thu nhỏ biểu đồ" : "Phóng to biểu đồ"}
              >
                <Eye className="w-4 h-4 lg:w-5 lg:h-5" />
              </button>
            </div>
          </div>
          
                     {/* Chart Container - Full width for laptop */}
           <div className={`chart-container chart-wrapper w-full transition-all duration-300 ${
             isChartFullscreen 
               ? 'h-[90vh]' 
               : 'h-64 sm:h-80 lg:h-96 xl:h-96 2xl:h-96'
           }`}>
            <Line key={chartKey} data={getRevenueChartData()} options={combinedChartOptions} />
          </div>
        </div>
      )}

      {/* Top Stores and Products Grid */}
      {!loading && !error && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          {/* Top Stores by Sales */}
          <div className="bg-white p-4 lg:p-6 rounded-xl shadow-lg border">
            <div className="flex items-center justify-between mb-4 lg:mb-6">
              <h3 className="text-base lg:text-lg font-semibold text-gray-900">Top cửa hàng bán nhiều nhất</h3>
              <button className="p-1.5 lg:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                <Download className="w-4 h-4 lg:w-5 lg:h-5" />
              </button>
            </div>
            
            <div className="space-y-3 lg:space-y-4">
              {getTopStoresBySales().length > 0 ? (
                getTopStoresBySales().map((store, index) => (
                  <div key={store.name} className="p-3 lg:p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2 lg:mb-3">
                      <div className="flex items-center space-x-2 lg:space-x-3">
                        <div className={`w-6 h-6 lg:w-8 lg:h-8 rounded-full flex items-center justify-center text-white font-bold text-xs lg:text-sm ${
                          index === 0 ? 'bg-yellow-500' : 
                          index === 1 ? 'bg-gray-400' : 
                          index === 2 ? 'bg-orange-500' : 'bg-blue-500'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <p className="text-sm lg:text-base font-medium text-gray-900">{store.name}</p>
                          <p className="text-xs lg:text-sm text-gray-600">{store.orders} đơn hàng</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm lg:text-base font-bold text-gray-900">{formatCurrency(store.revenue)}</p>
                                                 <p className="text-xs lg:text-sm text-gray-600">{store.items} sản phẩm</p>
                      </div>
                    </div>
                    
                    {/* Mini Chart */}
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 h-1.5 lg:h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-green-500 to-blue-500 rounded-full"
                          style={{ width: `${((store.orders) / 100) * 100}%` }}
                        ></div>
                      </div>
                                             <span className="text-xs text-gray-500">{store.orders} đơn hàng</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-4 text-center text-gray-500">
                  <Store className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p>Không có dữ liệu cửa hàng</p>
                </div>
              )}
            </div>
          </div>

                     {/* Top Products */}
           <div className="bg-white p-4 lg:p-6 rounded-xl shadow-lg border">
             <div className="flex items-center justify-between mb-4 lg:mb-6">
               <h3 className="text-base lg:text-lg font-semibold text-gray-900">Top sản phẩm bán chạy nhất</h3>
             </div>
            
            <div className="space-y-3 lg:space-y-4">
              {getTopProducts().length > 0 ? (
                getTopProducts().map((product, index) => (
                  <div key={product.id} className="p-3 lg:p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2 lg:mb-3">
                      <div className="flex items-center space-x-2 lg:space-x-3">
                        <div className={`w-6 h-6 lg:w-8 lg:h-8 rounded-full flex items-center justify-center text-white font-bold text-xs lg:text-sm ${
                          index === 0 ? 'bg-yellow-500' : 
                          index === 1 ? 'bg-gray-400' : 
                          index === 2 ? 'bg-orange-500' : 'bg-blue-500'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <p className="text-sm lg:text-base font-medium text-gray-900">{product.name}</p>
                          <p className="text-xs lg:text-sm text-gray-600">{product.sales} đã bán</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm lg:text-base font-bold text-gray-900">{formatCurrency(product.revenue)}</p>
                        <div className="flex items-center space-x-1">
                          {getGrowthIcon(product.growth)}
                          <span className={`text-xs lg:text-sm ${getGrowthColor(product.growth)}`}>
                            {product.growth > 0 ? '+' : ''}{product.growth}%
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Mini Chart */}
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 h-1.5 lg:h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                          style={{ width: `${((product.sales) / 50) * 100}%` }}
                        ></div>
                      </div>
                                             <span className="text-xs text-gray-500">{product.sales} đã bán</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-4 text-center text-gray-500">
                  <Package className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p>Không có dữ liệu sản phẩm</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

             {/* Quick Actions */}
       <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-4 lg:p-6 rounded-xl shadow-lg text-white">
         <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between space-y-4 lg:space-y-0">
           <div>
             <h3 className="text-lg lg:text-xl font-bold mb-2">Thông báo</h3>
             <p className="text-sm lg:text-base text-blue-100">Xem thông báo mới nhất</p>
           </div>
           <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
             <button 
               onClick={() => navigate('/seller/orders')}
               className="px-4 py-2 bg-white text-blue-600 hover:bg-gray-100 rounded-lg transition-colors text-sm lg:text-base"
             >
               Xem đơn hàng
             </button>
           </div>
         </div>
       </div>
    </div>
  );
};

export default SellerDashboard; 
